import { useCallback } from 'react'
import {
  ICreateOutboundAs2ConnectionConfigInput,
  ICreateOutboundFtpConnectionConfigInput,
  IUpdateOutboundAs2ConnectionConfigInput,
  IUpdateOutboundFtpConnectionConfigInput,
} from 'types/graphqlTypes'
import { useGlobalToast } from 'transfix-ui'
import { useCreateEdiOutboundAs2ConnectionConfigMutation } from '../graphql/CreateEdiOutboundAs2ConnectionConfig'
import { useCreateEdiOutboundFtpConnectionConfigMutation } from '../graphql/CreateEdiOutboundFtpConnectionConfig'
import { useUpdateEdiOutboundAs2ConnectionConfigMutation } from '../graphql/UpdateEdiOutboundAs2ConnectionConfig'
import { useUpdateEdiOutboundFtpConnectionConfigMutation } from '../graphql/UpdateEdiOutboundFtpConnectionConfig'

export const useEdiOutboundConnectionConfig = () => {
  const [
    createEdiOutboundFtpConnectionConfigMutation,
  ] = useCreateEdiOutboundFtpConnectionConfigMutation()

  const [
    createEdiOutboundAs2ConnectionConfigMutation,
  ] = useCreateEdiOutboundAs2ConnectionConfigMutation()

  const [
    updateEdiOutboundAs2ConnectionConfigMutation,
  ] = useUpdateEdiOutboundAs2ConnectionConfigMutation()

  const [
    updateEdiOutboundFtpConnectionConfigMutation,
  ] = useUpdateEdiOutboundFtpConnectionConfigMutation()

  const { openToast } = useGlobalToast()

  const createFtpOutboundConnectionConfig = useCallback(
    async (input: ICreateOutboundFtpConnectionConfigInput) => {
      try {
        const response = await createEdiOutboundFtpConnectionConfigMutation({
          variables: {
            input,
          },
          refetchQueries: ['EdiOutboundConnectionConfigs'],
        })

        const { data } = response || {}
        const { createEdiOutboundFtpConnectionConfig } = data || {}
        const { errors = [] } = createEdiOutboundFtpConnectionConfig || {}

        const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

        if (filteredErrors.length > 0) {
          openToast({
            toastMessage: filteredErrors.join(', '),
            toastType: 'alert',
            toastDuration: null,
          })
          return
        }

        openToast({
          toastMessage: 'Outbound FTP Connection Config created successfully.',
          toastType: 'default',
        })
      } catch (e) {
        openToast({ toastMessage: e.message, toastType: 'alert' })
      }
    },
    [createEdiOutboundFtpConnectionConfigMutation, openToast]
  )

  const createAs2OutboundConnectionConfig = useCallback(
    async (input: ICreateOutboundAs2ConnectionConfigInput) => {
      try {
        const response = await createEdiOutboundAs2ConnectionConfigMutation({
          variables: {
            input,
          },
          refetchQueries: ['EdiOutboundConnectionConfigs'],
        })

        const { data } = response || {}
        const { createEdiOutboundAs2ConnectionConfig } = data || {}
        const { errors = [] } = createEdiOutboundAs2ConnectionConfig || {}

        const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

        if (filteredErrors.length > 0) {
          openToast({
            toastMessage: filteredErrors.join(', '),
            toastType: 'alert',
            toastDuration: null,
          })
          return
        }

        openToast({
          toastMessage: 'Outbound AS2 Connection Config created successfully.',
          toastType: 'default',
        })
      } catch (e) {
        openToast({ toastMessage: e.message, toastType: 'alert' })
      }
    },
    [createEdiOutboundAs2ConnectionConfigMutation, openToast]
  )

  const updateAs2OutboundConnectionConfig = useCallback(
    async (input: IUpdateOutboundAs2ConnectionConfigInput) => {
      try {
        const response = await updateEdiOutboundAs2ConnectionConfigMutation({
          variables: {
            input,
          },
          refetchQueries: ['EdiOutboundConnectionConfigs'],
        })
        const { data } = response || {}
        const { updateEdiOutboundAs2ConnectionConfig } = data || {}
        const { errors = [] } = updateEdiOutboundAs2ConnectionConfig || {}

        const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

        if (filteredErrors.length > 0) {
          openToast({
            toastMessage: filteredErrors.join(', '),
            toastType: 'alert',
            toastDuration: null,
          })
          return
        }

        openToast({
          toastMessage: 'Outbound AS2 Connection Config updated successfully.',
          toastType: 'default',
        })
      } catch (e) {
        openToast({ toastMessage: e.message, toastType: 'alert' })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateEdiOutboundAs2ConnectionConfigMutation]
  )

  const updateFtpOutboundConnectionConfig = useCallback(
    async (input: IUpdateOutboundFtpConnectionConfigInput) => {
      try {
        const response = await updateEdiOutboundFtpConnectionConfigMutation({
          variables: {
            input,
          },
          refetchQueries: ['EdiOutboundConnectionConfigs'],
        })
        const { data } = response || {}
        const { updateEdiOutboundFtpConnectionConfig } = data || {}
        const { errors = [] } = updateEdiOutboundFtpConnectionConfig || {}

        const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

        if (filteredErrors.length > 0) {
          openToast({
            toastMessage: filteredErrors.join(', '),
            toastType: 'alert',
            toastDuration: null,
          })
          return
        }

        openToast({
          toastMessage: 'Outbound S/FTP Connection Config updated successfully.',
          toastType: 'default',
        })
      } catch (e) {
        openToast({ toastMessage: e.message, toastType: 'alert' })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateEdiOutboundAs2ConnectionConfigMutation]
  )

  return {
    createFtpOutboundConnectionConfig,
    createAs2OutboundConnectionConfig,
    updateAs2OutboundConnectionConfig,
    updateFtpOutboundConnectionConfig,
  }
}
